<script setup>
// This component name is a preexisting condition, disable inspection.
/* eslint vue/multi-word-component-names: 0 */
import { useHeadfulSetter } from '@/lib/hooks';
import i18n, { availableLocales } from '@/i18n';
import { useRoute, useRouter } from 'vue-router/composables';
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { parseToken } from '@/lib/functions';
import { accessAuth, getScreeningByUUID, resendSms, smsAuth } from '@/api';
import moment from 'moment';

const setHeadful = useHeadfulSetter();
setHeadful({
  title: 'E+POS - Login',
  description: i18n.t('title'),
});

const currentInstance = getCurrentInstance();
const isSmall = computed(() => currentInstance.proxy.$mq === 'small');

const router = useRouter();
const route = useRoute();

// Language set explicitly through the query, only if valid
const explicitLanguage = computed(() => {
  const routeLanguage = route.query['language']?.toString();
  return routeLanguage && availableLocales.includes(routeLanguage)
    ? routeLanguage
    : null;
});

// Current local storage token, or token from request
const token = ref(null);

function setToken(v) {
  sessionStorage.setItem('token', v);
  token.value = v;
  updateResend();
}
function clearToken() {
  token.value = null;
  sessionStorage.removeItem('token');
  updateResend();
}

const user = computed(() => (token.value ? parseToken(token.value) : null));
const resendTime = ref(null);

// Returns a counter in mm:ss format for when the next SMS
// can be sent based on the current user value. Will return
// null if the SMS can already be sent, or the future date
// is unknown.
function getResendDisplay() {
  const from = user.value?.retry_from;
  if (!from) {
    // If the retry from date is unknown, we'll let the user attempt
    // to get another SMS.
    return null;
  }

  const date = moment(from);
  const duration = date.diff(moment(), 'seconds');
  if (duration <= 0) {
    // Our retry_from date has passed.
    return null;
  }

  const mins = Math.floor(duration / 60);
  const secs = (duration % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
}

// Set to the setInterval ID later
let resendTimeout = null;
function updateResend() {
  resendTime.value = getResendDisplay();
}

// Feedback messages.
const confirm = ref(null);

// The warning contains either:
// - A string with a custom warning
// - A boolean to display the generic warning
const warning = ref(null);
function setFailed() {
  warning.value = i18n.t('failed');
}

//#region else-if states for the view body
// To be used as else-if view variables, "false" for the variables
// above each individual one is implied.
// Show the loading state if we have no warning to show
// and an email code or access code present.
const showLoading = computed(
  () => !warning.value && (emailCode.value || accessCode.value)
);

// We show a specific error if there was one returned from the API
// for the given access code, and we aren't already in the SMS login screen.
// (at which point no more emailCode will be present, because we'll have
// redirected away from it).
const showSpecificError = computed(
  () =>
    (emailCode.value || accessCode.value) &&
    warning.value &&
    warning.value !== true
);

const showSmsLogin = computed(() => user.value?.type === 'pretoken');
//#endregion

// Handles whatever the current user value is.
async function processToken(tokenValue) {
  warning.value = null;
  setToken(tokenValue);
  const u = user.value;

  if (!u) {
    clearToken();
    return;
  }

  // The token has type "token" or "pretoken" depending on
  // whether 2FA is still necessary.
  if (u.type === 'token') {
    // Open the screening
    await router.replace('/');
    return;
  }

  if (u.type === 'pretoken' && emailCode.value) {
    // Get rid of the screening UUID in the query string. This way, if somebody leaves
    // a tab open, it won't continuously spam new text messages when reloading.
    // The token stays in session storage, so we can refresh this page and stay on
    // the SMS code screen.
    await router.replace('/login');
    focusSmsInput();
  }
}

// An email code is provided as a query string via a link that we send
// to patients to open the screening. After the email code, there will
// generally have to be a two-factor login using an SMS code. An additional
// two factor code may be specified directly in the `c` query parameter,
// which enables opening the screening directly.
const emailCode = computed(() => route.query['screening']?.toString());
const twoFactorCode = computed(() => route.query['c']?.toString());
const smsLoginCodeInput = ref(null);
function focusSmsInput() {
  nextTick(() => smsLoginCodeInput.value?.focus());
}
async function loginByEmailCode() {
  if (!emailCode.value) {
    return;
  }

  clearToken();

  try {
    const response = await getScreeningByUUID(
      emailCode.value,
      twoFactorCode.value,
      explicitLanguage.value
    );
    await processToken(response.data);
  } catch (e) {
    if (e.response?.status === 422 && e.response.data?.errors?.problem) {
      warning.value = e.response.data.errors.problem[0];
    } else {
      warning.value = true;
    }
  }
}

// An SMS code is sent after email login
const smsCode = ref('');
async function loginBySmsCode() {
  if (!smsCode.value) {
    return;
  }

  warning.value = null;
  try {
    const response = await smsAuth(smsCode.value);
    await processToken(response.data);
  } catch {
    setFailed();
  }
}

async function resendSmsCode() {
  warning.value = null;
  try {
    const response = await resendSms(explicitLanguage.value);
    await processToken(response.data);
    focusSmsInput();

    if (user.value?.message_sent) {
      confirm.value = i18n.t('new-code-sent');
    }
  } catch {
    setFailed();
  }
}

// An access code is an OTP generated between the admin/patient service
// that will log the screening in without 2FA. Generally only used in
// debug / developer scenarios.
const accessCode = computed(() => route.query['access']?.toString());
async function loginByAccessCode() {
  if (!accessCode.value) {
    return;
  }

  warning.value = null;
  try {
    const response = await accessAuth(accessCode.value);
    await processToken(response.data);
  } catch {
    setFailed();
  }
}

onMounted(() => {
  resendTimeout = setInterval(updateResend, 1000);

  if (accessCode.value) {
    loginByAccessCode();
    return;
  }

  if (emailCode.value) {
    loginByEmailCode();
    return;
  }

  // Process any token that's currently configured
  processToken(sessionStorage.getItem('token'));
});

onBeforeUnmount(() => {
  if (resendTimeout !== null) {
    clearInterval(resendTimeout);
  }
});
</script>

<template>
  <div class="landing-page">
    <div class="login ml-auto mr-auto">
      <div class="card" title="Login">
        <div class="card-header">
          <img src="@/assets/Logo_EPOS.png" class="logo" alt="EPOS logo" />
        </div>
        <div class="card-body">
          <template v-if="showLoading">
            {{ /* If an email code is present, we're either loading the screening, */ }}
            {{ /* or we have attempted to load the screening unsuccessfully. */ }}
            {{ /* After the first successful exchange of a one-factor token, */ }}
            {{ /* the e-mail code is removed from the URL and will no longer be present. */ }}
            <p>
              {{ $t('loading') }}
              <i class="fal fa-spinner fa-spin ml-4"></i>
            </p>
          </template>
          <template v-else-if="showSpecificError">
            <p>{{ warning }}</p>
          </template>
          <template v-else-if="showSmsLogin">
            <p>{{ $t('form-line-one', { phone: user.mobile }) }}</p>

            <form @submit.prevent="loginBySmsCode">
              <div class="form-group">
                <input
                  autofocus
                  ref="smsLoginCodeInput"
                  required
                  type="text"
                  autocomplete="one-time-code"
                  pattern="[0-9]*"
                  v-model="smsCode"
                  class="form-control"
                  inputmode="numeric"
                  id="sms-code"
                  aria-describedby="verification_code"
                  :placeholder="$t('received-code-placeholder')"
                />
              </div>

              <div
                :class="
                  isSmall ? 'sms-code-buttons-mobile' : 'sms-code-buttons'
                "
                class="mb-3"
              >
                <button
                  type="submit"
                  class="btn btn-novacair-light"
                  :class="{ 'btn-mobile': isSmall }"
                >
                  {{ $t('continue-button') }}
                </button>
              </div>
            </form>

            <p>
              {{ $t('no-code-received') }}
              <template v-if="resendTime">
                <span class="resend-timeout">{{
                  $t('resend-in', { time: resendTime })
                }}</span>
              </template>
              <template v-else>
                <a @click="resendSmsCode()" class="resend-link">
                  {{ $t('resend-code-link') }}</a
                >
              </template>
            </p>

            <div class="alert alert-danger" v-if="warning">
              {{ warning }}
            </div>
            <div class="alert alert-info" v-if="confirm">
              {{ confirm }}
            </div>
          </template>
          <template v-else>
            {{ /* Show the generic error if we don't know what else to do. */ }}
            <p>{{ $t('no-code-line-one') }}</p>
            <p>{{ $t('no-code-line-two') }}</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.login {
  padding-top: 60px;
  max-width: 550px;
}

.logo {
  height: 30px;
  margin: 10px 0 10px 0;
}

.btn-mobile {
  width: 250px;
}

.resend-mobile {
  margin-left: 0 !important;
  margin-top: 15px;
}

.sms-code-buttons-mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
}

.btn-novacair-light {
  border: 1px solid $primary;
  background-color: white;
  color: black;
  padding: 5px 30px;
}

.alert {
  margin-top: 20px;
}

.resend {
  margin-left: 10px;
}

.resend-link {
  cursor: pointer;
  font-weight: bold;
  color: #154866 !important;
}

.resend-timeout {
  font-weight: bold;
  color: #85898a;
  cursor: wait;
}

.buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.alert-info {
  color: #154866 !important;
}
</style>
